/**
 * Attribution Bridge — Slice 1 AT-01c.
 *
 * PWA-side wrapper for the 5 new `OWNYOU_PWA_*` request types + subscription
 * to the `window` `CustomEvent('ownyou:broadcast')` dispatched by the
 * extension's bridge.js (AT-01d). Domain-cohesive sibling alongside
 * `extension-credential-sync.ts` and `engagement-profile-bridge.ts`.
 *
 * The 5 request types (PWA → extension, routed by background.js's PWA
 * branch — see AT-01d for the extension wiring):
 *
 *   OWNYOU_PWA_GET_INSTALL_CONSENT_STATE  → { recorded, recorded_at? }
 *   OWNYOU_PWA_RECORD_INSTALL_CONSENT      → { ok, recorded_at? } | { ok: false, error }
 *   OWNYOU_PWA_GET_DISCLOSURE_SNAPSHOT     → { disclosure }
 *   OWNYOU_PWA_GET_EARNINGS                → { paymentLog }
 *   OWNYOU_PWA_GET_DELIVERY_LOG            → { deliveryLog }
 *
 * Three outbound broadcast types subscribed on the window:
 *   ownyou:broadcast detail.type === 'OWNYOU_DELIVERY_COMPLETED'  (AT-04 emits)
 *   ownyou:broadcast detail.type === 'OWNYOU_DELIVERY_FAILED'     (AT-04 emits)
 *   ownyou:broadcast detail.type === 'OWNYOU_PAYMENT_RECEIVED'    (AT-06 emits)
 *
 * AT-01c lands the wire substrate ONLY — the semantic broadcast events
 * are emitted by AT-04 (delivery) and AT-06 (payment).
 */

const DEFAULT_TIMEOUT_MS = 5_000;

export interface InstallConsentState {
  recorded: boolean;
  recorded_at?: number;
}

export interface RecordInstallConsentInput {
  protocol_version: string;
  disclosure_policy_version: string;
  trust_registry_version: string;
}

export type RecordInstallConsentResult =
  | { ok: true; recorded_at: number }
  | { ok: false; error: string };

export interface DisclosureSnapshot {
  iab_categories: Array<{ id: number; label?: string; confidence?: number }>;
  purchase_intent: Array<{ id: number; label?: string; confidence?: number }>;
  demographics: Record<string, string>;
  household: Record<string, string>;
  reputation_level: string | null;
  assurance_summary: { max_assurance_class?: string };
}

export interface PaymentLogEntry {
  tx_hash: `0x${string}`;
  campaign_ref: `0x${string}`;
  rotation_counter: number;
  recipient: `0x${string}`;
  amount: string;
  block_number?: number;
  observed_at: number;
}

export interface DeliveryLogEntry {
  delivery_event_id: string;
  campaign_ref: `0x${string}`;
  rotation_counter: number;
  publisher_origin?: string;
  state: string;
  ack_status?: number;
  ack_reason?: string;
  payment_reference?: `0x${string}`;
  recorded_at: number;
}

export type BroadcastType =
  | 'OWNYOU_DELIVERY_COMPLETED'
  | 'OWNYOU_DELIVERY_FAILED'
  | 'OWNYOU_PAYMENT_RECEIVED';

export interface BroadcastPayload {
  type: BroadcastType;
  [key: string]: unknown;
}

export type BroadcastListener = (payload: BroadcastPayload) => void;

interface OwnYouRequestEnvelope {
  type: 'OWNYOU_REQUEST';
  source: 'ownyou-pwa';
  requestId: string;
  payload: {
    type: string;
    [key: string]: unknown;
  };
}

interface OwnYouResponseEnvelope {
  type: 'OWNYOU_RESPONSE';
  requestId: string;
  payload?: unknown;
  error?: string;
}

function randomId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `req-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function sendRequest<T>(
  payload: { type: string; [key: string]: unknown },
  timeoutMs: number,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const requestId = randomId();

    const handler = (event: MessageEvent<OwnYouResponseEnvelope>) => {
      if (
        event.data?.type === 'OWNYOU_RESPONSE' &&
        event.data.requestId === requestId
      ) {
        window.removeEventListener('message', handler);
        clearTimeout(timer);
        if (event.data.error) {
          reject(new Error(event.data.error));
        } else {
          resolve(event.data.payload as T);
        }
      }
    };

    window.addEventListener('message', handler);

    const timer = setTimeout(() => {
      window.removeEventListener('message', handler);
      reject(
        new Error(
          'Request timeout — is the OwnYou extension installed and connected to app.ownyou.app?',
        ),
      );
    }, timeoutMs);

    const envelope: OwnYouRequestEnvelope = {
      type: 'OWNYOU_REQUEST',
      source: 'ownyou-pwa',
      requestId,
      payload,
    };
    window.postMessage(envelope, '*');
  });
}

export interface AttributionBridgeOptions {
  /** Override the request timeout (default 5_000 ms). */
  timeoutMs?: number;
  /** Window object — injected for tests that use jsdom or a custom event target. */
  window?: typeof globalThis.window;
}

export class AttributionBridge {
  private readonly timeoutMs: number;
  private readonly win: typeof globalThis.window;
  private readonly listeners = new Map<BroadcastType, Set<BroadcastListener>>();
  private windowListener: ((event: Event) => void) | null = null;

  constructor(opts: AttributionBridgeOptions = {}) {
    this.timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    this.win = opts.window ?? (globalThis.window as typeof globalThis.window);
    this.attachBroadcastListener();
  }

  async getInstallConsentState(): Promise<InstallConsentState> {
    return sendRequest<InstallConsentState>(
      { type: 'OWNYOU_PWA_GET_INSTALL_CONSENT_STATE' },
      this.timeoutMs,
    );
  }

  async recordInstallConsent(
    input: RecordInstallConsentInput,
  ): Promise<RecordInstallConsentResult> {
    return sendRequest<RecordInstallConsentResult>(
      {
        type: 'OWNYOU_PWA_RECORD_INSTALL_CONSENT',
        protocol_version: input.protocol_version,
        disclosure_policy_version: input.disclosure_policy_version,
        trust_registry_version: input.trust_registry_version,
      },
      this.timeoutMs,
    );
  }

  async getDisclosureSnapshot(): Promise<{ disclosure: DisclosureSnapshot }> {
    return sendRequest<{ disclosure: DisclosureSnapshot }>(
      { type: 'OWNYOU_PWA_GET_DISCLOSURE_SNAPSHOT' },
      this.timeoutMs,
    );
  }

  async getEarnings(): Promise<{ paymentLog: PaymentLogEntry[] }> {
    return sendRequest<{ paymentLog: PaymentLogEntry[] }>(
      { type: 'OWNYOU_PWA_GET_EARNINGS' },
      this.timeoutMs,
    );
  }

  async getDeliveryLog(): Promise<{ deliveryLog: DeliveryLogEntry[] }> {
    return sendRequest<{ deliveryLog: DeliveryLogEntry[] }>(
      { type: 'OWNYOU_PWA_GET_DELIVERY_LOG' },
      this.timeoutMs,
    );
  }

  subscribe(type: BroadcastType, listener: BroadcastListener): () => void {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }
    this.listeners.get(type)!.add(listener);
    return () => this.listeners.get(type)?.delete(listener);
  }

  destroy(): void {
    if (this.windowListener) {
      this.win.removeEventListener('ownyou:broadcast', this.windowListener);
      this.windowListener = null;
    }
    this.listeners.clear();
  }

  private attachBroadcastListener(): void {
    this.windowListener = (event: Event) => {
      const detail = (event as CustomEvent<BroadcastPayload>).detail;
      if (!detail || typeof detail !== 'object' || !('type' in detail)) return;
      const set = this.listeners.get(detail.type as BroadcastType);
      if (!set) return;
      for (const listener of set) {
        try {
          listener(detail);
        } catch {
          // Swallow listener errors — one listener's bug shouldn't propagate.
        }
      }
    };
    this.win.addEventListener('ownyou:broadcast', this.windowListener);
  }
}
